/**
 * Product category PURE rules (B0.2' extraction from
 * `mocks/handlers/productCategory.ts` — S0.4 5-capable taxonomy).
 *
 * Moved verbatim: subtree math, the create/reparent/deactivate/level guards
 * (depth hard-cap 5, enabled-level create gate, code-unique-forever, no
 * cycles, replacement-required, L1..L3 locked, nav-label denylist) and the
 * bilingual breadcrumb — all list-injected. The live stores, audit writing
 * and MSW routes stay in `mocks/handlers/productCategory.ts`; the
 * `selectableCategories` predicate keeps living in `src/utils/categoryUtils.ts`
 * (Slice 4.1 ownership) — see packages/domain/EXTRACTION-CENSUS.md.
 */

import type {
  CategoryGuardError,
  CategoryLevelConfig,
  ProductCategory,
} from '@/types/api/productCategory';
import {
  MAX_CATEGORY_DEPTH,
  MIN_ENABLED_CATEGORY_DEPTH,
  NAV_GROUP_LABEL_DENYLIST,
} from '@/types/api/productCategory';

/** Ids of `rootId` plus every descendant (safe on inconsistent parent data). */
export function subtreeIds(nodes: ProductCategory[], rootId: string): Set<string> {
  const ids = new Set<string>([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const node of nodes) {
      if (node.parentId && ids.has(node.parentId) && !ids.has(node.id)) {
        ids.add(node.id);
        grew = true;
      }
    }
  }
  return ids;
}

/** Levels spanned by the subtree rooted at `rootId` (1 = leaf, 2 = has children, …). */
export function subtreeHeight(nodes: ProductCategory[], rootId: string): number {
  const ids = subtreeIds(nodes, rootId);
  const root = nodes.find((n) => n.id === rootId);
  if (!root) return 0;
  let deepest = root.depth;
  for (const node of nodes) {
    if (ids.has(node.id) && node.depth > deepest) deepest = node.depth;
  }
  return deepest - root.depth + 1;
}

/**
 * Create guard: parent must exist and be active; depth hard-capped at 5;
 * create blocked past the enabled max (L4/L5 hidden by default); codes are
 * unique across ALL nodes — active AND inactive — and never reused.
 */
export function guardCreate(
  nodes: ProductCategory[],
  levels: CategoryLevelConfig[],
  input: { parentId: string | null; code: string }
): CategoryGuardError | null {
  let depth = 1;
  if (input.parentId !== null) {
    const parent = nodes.find((n) => n.id === input.parentId);
    if (!parent) return 'PARENT_NOT_FOUND';
    if (!parent.isActive) return 'PARENT_INACTIVE';
    depth = parent.depth + 1;
  }
  if (depth > MAX_CATEGORY_DEPTH) return 'DEPTH_EXCEEDED';
  if (!levels.find((l) => l.level === depth)?.enabled) return 'LEVEL_DISABLED';
  const code = input.code.trim().toLowerCase();
  if (!code) return 'CODE_TAKEN';
  if (nodes.some((n) => n.code.toLowerCase() === code)) return 'CODE_TAKEN';
  return null;
}

/**
 * Reparent guard: no cycles (new parent inside own subtree) and rejected if
 * ANY descendant would exceed depth 5. Disabled levels do NOT block reparent —
 * hidden-level nodes stay readable (hide-don't-disable); only the hard cap holds.
 */
export function guardReparent(
  nodes: ProductCategory[],
  nodeId: string,
  newParentId: string | null
): CategoryGuardError | null {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return 'NODE_NOT_FOUND';
  let newDepth = 1;
  if (newParentId !== null) {
    if (subtreeIds(nodes, nodeId).has(newParentId)) return 'CYCLE';
    const parent = nodes.find((n) => n.id === newParentId);
    if (!parent) return 'PARENT_NOT_FOUND';
    if (!parent.isActive) return 'PARENT_INACTIVE';
    newDepth = parent.depth + 1;
  }
  if (newDepth + subtreeHeight(nodes, nodeId) - 1 > MAX_CATEGORY_DEPTH) {
    return 'SUBTREE_DEPTH_EXCEEDED';
  }
  return null;
}

/** Apply a (guarded) reparent — recomputes depth + materialized path for the whole subtree. */
export function applyReparent(
  nodes: ProductCategory[],
  nodeId: string,
  newParentId: string | null
): ProductCategory[] {
  const byId = new Map(nodes.map((n) => [n.id, { ...n }]));
  const node = byId.get(nodeId);
  if (!node) return nodes;
  const parent = newParentId ? byId.get(newParentId) : undefined;
  node.parentId = newParentId;
  node.depth = ((parent?.depth ?? 0) + 1) as ProductCategory['depth'];
  node.path = parent ? `${parent.path}/${node.id}` : node.id;

  // Breadth-first recompute below the moved node
  let frontier = [node];
  while (frontier.length > 0) {
    const next: ProductCategory[] = [];
    for (const current of frontier) {
      for (const child of byId.values()) {
        if (child.parentId === current.id) {
          child.depth = (current.depth + 1) as ProductCategory['depth'];
          child.path = `${current.path}/${child.id}`;
          next.push(child);
        }
      }
    }
    frontier = next;
  }
  return nodes.map((n) => byId.get(n.id) ?? n);
}

/**
 * Deactivate guard: whenever products are assigned anywhere in the subtree a
 * replacement is required; the replacement must be an active node outside the
 * deactivated subtree. No hard delete exists.
 */
export function guardDeactivate(
  nodes: ProductCategory[],
  nodeId: string,
  assignedProductCount: number,
  replacementId?: string
): CategoryGuardError | null {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return 'NODE_NOT_FOUND';
  if (assignedProductCount > 0 && !replacementId) return 'REPLACEMENT_REQUIRED';
  if (replacementId) {
    const replacement = nodes.find((n) => n.id === replacementId);
    if (!replacement || !replacement.isActive || subtreeIds(nodes, nodeId).has(replacementId)) {
      return 'REPLACEMENT_INVALID';
    }
  }
  return null;
}

/**
 * Level-config guard: L1..L3 can never be disabled (3 seeded defaults), and no
 * level label may duplicate a nav group name (S0.4 rule; the L1 default was
 * renamed off 系統/System for this exact collision).
 */
export function guardLevelUpdate(
  level: number,
  patch: { labelEn?: string; labelZh?: string; enabled?: boolean }
): CategoryGuardError | null {
  if (patch.enabled === false && level <= MIN_ENABLED_CATEGORY_DEPTH) return 'CORE_LEVEL_LOCKED';
  const denied = (label?: string): boolean =>
    label !== undefined &&
    NAV_GROUP_LABEL_DENYLIST.some((nav) => nav.toLowerCase() === label.trim().toLowerCase());
  if (denied(patch.labelEn) || denied(patch.labelZh)) return 'NAV_LABEL_COLLISION';
  return null;
}

/** Bilingual-capable breadcrumb along `path`, e.g. 煞車系統 › 煞車來令片 › 陶瓷來令. */
export function buildCategoryBreadcrumb(
  nodes: ProductCategory[],
  categoryId: string,
  lang: 'en' | 'zh' = 'en'
): string {
  const target = nodes.find((n) => n.id === categoryId);
  if (!target) return '';
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return target.path
    .split('/')
    .map((id) => {
      const node = byId.get(id);
      if (!node) return id;
      return lang === 'zh' ? node.nameZh : node.nameEn;
    })
    .join(' › ');
}
